// src/layouts/components/WebMenu.jsx

import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';
import AVIcon from '../../components/icons/AllVoicedIcons';
import {
  isServiceRoutePath,
  serviceRoutes,
} from '../../navigation/publicServiceRoutes';
import {
  aboutHeaderRoutes,
  isAboutRoutePath,
} from '../../navigation/publicAboutRoutes';
import {
  isPublicNavigationItemActive,
  publicHeaderActions,
  publicPrimaryNavigationItems,
} from '../../navigation/publicNavigationRoutes';

const WebMenu = ({ mode }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const submenuRefs = useRef({});
  const submenuToggleRefs = useRef({});

  const isServicesActive = isServiceRoutePath(location.pathname);
  const isAboutActive = isAboutRoutePath(location.pathname);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
      if (window.innerWidth >= 900) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!openSubmenuId) return undefined;

    const handlePointerDown = (event) => {
      const openSubmenuRef = submenuRefs.current[openSubmenuId];

      if (
        openSubmenuRef &&
        !openSubmenuRef.contains(event.target)
      ) {
        setOpenSubmenuId(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenSubmenuId(null);
        submenuToggleRefs.current[openSubmenuId]?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSubmenuId]);

  useEffect(() => {
    setOpenSubmenuId(null);
  }, [location.pathname]);

  const menuItems = publicPrimaryNavigationItems;
  const headerActions = publicHeaderActions;

  const closeMenusAfterNavigation = () => {
    setOpenSubmenuId(null);
    setMenuOpen(false);
  };

  const getNavigationLabel = (item) => (
    t(item.translationKey, item.label)
  );

  const getNavLinkClassName = (item) => (
    isPublicNavigationItemActive(item, location.pathname)
      ? 'menu-link menu-link--active'
      : 'menu-link'
  );

  const getSubmenuConfig = (item) => {
    if (item.type === 'services') {
      return {
        id: 'services',
        routes: serviceRoutes,
        active: isServicesActive,
        label: getNavigationLabel(item),
      };
    }

    if (item.type === 'about') {
      return {
        id: 'about',
        routes: aboutHeaderRoutes,
        active: isAboutActive,
        label: getNavigationLabel(item),
      };
    }

    return null;
  };

  const getRouteLabel = (route) => (
    route.translationKey
      ? t(route.translationKey, route.label)
      : route.label
  );

  const toggleSubmenu = (submenuId) => {
    setOpenSubmenuId((currentSubmenuId) => (
      currentSubmenuId === submenuId ? null : submenuId
    ));
  };

  const renderSubmenuLinks = (routes) => (
    routes.map((route) => (
      <li key={route.path}>
        <NavLink
          to={route.path}
          className={({ isActive }) => (
            isActive ? 'menu-link menu-link--active' : 'menu-link'
          )}
          onClick={closeMenusAfterNavigation}
        >
          {getRouteLabel(route)}
        </NavLink>
      </li>
    ))
  );

  // 🖥️ Menú escrit (només desktop)
  if (mode === 'desktop' && !isMobile) {
    return (
      <nav className="web-menu">
        <ul className="menu-links">
          {menuItems.map((item) => {
            const submenuConfig = getSubmenuConfig(item);

            if (submenuConfig) {
              const isSubmenuOpen = openSubmenuId === submenuConfig.id;

              return (
                <li
                  key={item.path}
                  className={`menu-item menu-item--with-submenu menu-item--${submenuConfig.id} ${
                    submenuConfig.active ? 'menu-item--active' : ''
                  }`}
                  ref={(node) => {
                    submenuRefs.current[submenuConfig.id] = node;
                  }}
                >
                  <div className="menu-link-group">
                    <NavLink
                      to={item.path}
                      className={() => (
                        submenuConfig.active
                          ? 'menu-link menu-link--active'
                          : 'menu-link'
                      )}
                      onClick={() => setOpenSubmenuId(null)}
                    >
                      {getNavigationLabel(item)}
                    </NavLink>

                    <button
                      type="button"
                      className={`public-submenu-toggle ${submenuConfig.id}-submenu-toggle`}
                      aria-label={`Open ${submenuConfig.label} menu`}
                      aria-haspopup="true"
                      aria-expanded={isSubmenuOpen}
                      aria-controls={`${submenuConfig.id}-navigation-submenu`}
                      onClick={() => toggleSubmenu(submenuConfig.id)}
                      ref={(node) => {
                        submenuToggleRefs.current[submenuConfig.id] = node;
                      }}
                    >
                      <span aria-hidden="true">▾</span>
                    </button>
                  </div>

                  <ul
                    id={`${submenuConfig.id}-navigation-submenu`}
                    className={`header-dropdown-menu public-submenu ${submenuConfig.id}-submenu ${
                      isSubmenuOpen ? 'open' : ''
                    }`}
                    aria-label={submenuConfig.label}
                  >
                    {renderSubmenuLinks(submenuConfig.routes)}
                  </ul>
                </li>
              );
            }

            return (
              <li key={item.path} className="menu-item">
                <NavLink
                  to={item.path}
                  className={() => getNavLinkClassName(item)}
                >
                  {getNavigationLabel(item)}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="web-menu-actions" aria-label="Application entry">
          {headerActions.map((action) => (
            <Link
              key={action.id}
              to={action.path}
              className="web-menu-action-link"
            >
              {getNavigationLabel(action)}
            </Link>
          ))}
        </div>
      </nav>
    );
  }

  // 📱 Menú hamburguesa (només mòbil)
  if (mode === 'mobile' && isMobile) {
    return (
      <nav className="web-menu">
        <button
          className="menu-icon"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation-menu"
        >
          {menuOpen ? (
            <AVIcon icon="close" size={30} color="#209385" />
          ) : (
            <AVIcon icon="menu" size={30} color="#209385" />
          )}
        </button>

        <ul
          id="mobile-navigation-menu"
          className={`header-dropdown-menu is-left is-wide ${
            menuOpen ? 'open' : ''
          }`}
        >
          {menuItems.map((item) => {
            const submenuConfig = getSubmenuConfig(item);

            if (submenuConfig) {
              const isSubmenuOpen = openSubmenuId === submenuConfig.id;

              return (
                <li
                  key={item.path}
                  className={`mobile-submenu-menu mobile-${submenuConfig.id}-menu ${
                    submenuConfig.active ? 'mobile-submenu-menu--active' : ''
                  }`}
                >
                  <button
                    type="button"
                    className={`mobile-submenu-toggle mobile-${submenuConfig.id}-toggle`}
                    aria-label={`Open ${submenuConfig.label} menu`}
                    aria-haspopup="true"
                    aria-expanded={isSubmenuOpen}
                    aria-controls={`mobile-${submenuConfig.id}-submenu`}
                    onClick={() => toggleSubmenu(submenuConfig.id)}
                  >
                    <span>{getNavigationLabel(item)}</span>
                    <span aria-hidden="true">▾</span>
                  </button>

                  <ul
                    id={`mobile-${submenuConfig.id}-submenu`}
                    className={`mobile-submenu mobile-${submenuConfig.id}-submenu ${
                      isSubmenuOpen ? 'open' : ''
                    }`}
                    aria-label={submenuConfig.label}
                  >
                    {renderSubmenuLinks(submenuConfig.routes)}
                  </ul>
                </li>
              );
            }

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={() => getNavLinkClassName(item)}
                  onClick={closeMenusAfterNavigation}
                >
                  {getNavigationLabel(item)}
                </NavLink>
              </li>
            );
          })}

          {headerActions.map((action) => (
            <li key={action.id} className="mobile-menu-action">
              <Link
                to={action.path}
                className="mobile-menu-action-link"
                onClick={closeMenusAfterNavigation}
              >
                {getNavigationLabel(action)}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return null;
};

export default WebMenu;
